import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { displayTitle } from "@/lib/naming";
import { Loader2, MessageSquare } from "lucide-react";

export default function SubmitRevisionDialog({ open, onOpenChange, clientId, deliverable, feedback, onCreated }) {
    const [form, setForm] = useState({ agency_reply: "", file_name: "", link: "", description: "", note: "" });
    const [loading, setLoading] = useState(false);

    if (!deliverable) return null;
    const nextVersion = (deliverable.versions?.length || 0) + 1;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post(
                `/clients/${clientId}/deliverables/${deliverable.deliverable_id}/versions`,
                form,
            );
            toast.success(`Revision ${nextVersion} submitted for approval`, {
                description: "Your client has been notified.",
            });
            setForm({ agency_reply: "", file_name: "", link: "", description: "", note: "" });
            onCreated?.(data);
        } catch (err) {
            toast.error("Could not submit revision", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px] rounded-xl border-slate-200">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">
                        Submit Revision {nextVersion} of {displayTitle(deliverable.title)}
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        The previous version stays in history. Your client sees Revision {nextVersion} as the current version to review.
                    </DialogDescription>
                </DialogHeader>

                {feedback && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                        <div className="flex items-center gap-2">
                            <MessageSquare className="w-3.5 h-3.5 text-rose-600" />
                            <p className="text-xs font-semibold uppercase tracking-wider text-rose-700">Client feedback on Revision {nextVersion - 1}</p>
                        </div>
                        <p className="mt-1.5 text-sm text-rose-900 whitespace-pre-wrap leading-relaxed">{feedback}</p>
                    </div>
                )}

                <form onSubmit={submit} className="space-y-4 pt-2">
                    <div className="space-y-2">
                        <Label className="text-slate-700">Reply to the client (optional)</Label>
                        <Textarea rows={3} value={form.agency_reply}
                                  onChange={(e) => setForm({ ...form, agency_reply: e.target.value })}
                                  data-testid="revision-reply" className="ring-terra"
                                  placeholder="A short note about what changed in this version…" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label className="text-slate-700">File name (optional)</Label>
                            <Input value={form.file_name}
                                   onChange={(e) => setForm({ ...form, file_name: e.target.value })}
                                   data-testid="revision-file" className="ring-terra"
                                   placeholder="homepage-v2.fig" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-700">Link (optional)</Label>
                            <Input type="url" value={form.link}
                                   onChange={(e) => setForm({ ...form, link: e.target.value })}
                                   data-testid="revision-link" className="ring-terra"
                                   placeholder="https://…" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-slate-700">Update description (optional)</Label>
                        <Textarea rows={2} value={form.description}
                                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                                  data-testid="revision-description" className="ring-terra"
                                  placeholder="Leave blank to keep the existing description." />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={() => onOpenChange(false)}
                                data-testid="revision-cancel"
                                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading}
                                data-testid="revision-submit"
                                className="btn-terra rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70">
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Revision {nextVersion}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
